import React, {ReactElement, ReactNode} from 'react';
import {
    Sortable,
    Hideable,
    Positionable,
    ViewIdentifiable,
    OrderBy,
    UserViewSettings, UserViewSettingsList
} from "./ViewSettings";
import {Language, LanguageProp} from "../../config/LanguageConfig";
import {decimalToHM, formatDateTime} from "../../helpers/DateTimeHelpers";
import {Denseable} from "./ComponentBehavioural";
import {ColumnListState} from "../../reducers/common/ViewSettingsReducer";
import {SortListItem} from "../../components/common/data-sort/SortList";

/**
 * Enumeration of supported data types for table columns.
 * These types determine how data is formatted and rendered within the table cells.
 */
export const COLUMN_TYPES = {
    plain: 0,
    money: 1,
    number: 2,
    download: 3,
    address: 4,
    detailLink: 5,
    reportSolvency: 6,
    bool: 7,
    date: 8,
    dateTime: 9,
    workingHours: 10,
    lockStatus: 11,
    renderCell: 12
};

/**
 * DATA ACCESSORS
 *
 * predefined accessor functions for common use cases
 */
/**
 * Extracts the task name from the timesheet data.
 *
 * This utility function is designed to retrieve the name of a task from
 * the provided cell data accessor properties. It facilitates data access
 * for components or logic that require task-specific information within
 * a timesheet context.
 *
 * The function assumes that the task information is nested within the structure
 * of the row data and targets the `name` property of the `task` object.
 *
 * @param data - An object containing accessor properties, such as row data,
 *               which is expected to follow the defined structure including
 *               `rowData.task.name`.
 * @return The name of the task as a string.
 * @throws Will throw an error if the expected `task` or `name` properties are
 *         undefined or inaccessible due to an invalid data structure.
 */
export const dataAccessorTimesheetTask = ( data: CellDataAccessorProps ) => data.rowData.task.name;
/**
 * Combines project details into a single descriptive string.
 *
 * This function extracts and concatenates key project information to create a descriptive label
 * that can be used for display in the UI or logging purposes.
 *
 * @param data - An object containing project-related data encapsulated within `rowData`.
 * The `data` parameter includes properties that reference project information such as
 * the project's unique identifier (`projectNumber`) and a brief summary (`shortDescription`).
 * These properties are assumed to be safely accessible within the structure of `CellDataAccessorProps`.
 *
 * @returns A string formed by combining the `projectNumber` and `shortDescription` with a space
 * in between. The result is a succinct representation of the project details.
 *
 * @throws Will throw an error if the `rowData.project` structure is undefined or malformed,
 * as the function expects specific properties to always be present.
 */
export const dataAccessorProject = ( data: CellDataAccessorProps ) => {
    if( data.rowData.project.projectId.rawValue === 0 ) {
        return '-';
    } else {
        return data.rowData.project.projectNumber + ' ' + data.rowData.project.shortDescription;
    }
}

/**
 * Retrieves and formats the full name of an employee from the provided data object.
 *
 * This function is designed to extract the employee's first and last name, formatting them as "LastName, FirstName".
 * It is helpful for displaying employee names in a standardized way within applications.
 *
 * @param data - An object containing the cell data, including row and employee information.
 *               The `rowData` property must include an `employee` object with `firstName` and `lastName` properties.
 * @returns A string representing the employee's full name, formatted as "LastName, FirstName".
 * @throws If the required properties `rowData`, `employee`, `firstName`, or `lastName` are missing or undefined.
 */
export const dataAccessorEmployee = ( data: CellDataAccessorProps ) =>
    data.rowData.employee.lastName + ', ' + data.rowData.employee.firstName;

/**
 * Formats timesheet start and end times into a multi-line display.
 *
 * @param data - The cell data containing startAt and finishAt timestamps.
 * @returns A ReactElement displaying the formatted start and end times.
 */
export const dataAccessorTimesheetTime = ( data: CellDataAccessorProps) => {

    const start = new Date( data.rowData.startAt );
    const end = new Date( data.rowData.finishAt );
    return (
        <div>
            { formatDateTime( start, data.language )}
            <br />
            { formatDateTime(end, data.language ) }
        </div>
    );
};
/**
 * gets hours of work prop from row data and returns the formatted value as string
 * @param data
 */
export const dataAccessorWorkingHours = ( data: CellDataAccessorProps ) => decimalToHM( data.rowData.hoursOfWork );


/**
 * Builds and customizes columns for a data table or grid.
 *
 * This class is designed to simplify the creation and configuration of columns
 * tailored to business needs, including visual alignment, behavior, and rendering logic.
 */
export class ColumnBuilder {

    private headline = "";
    private field = "";
    private type = COLUMN_TYPES.plain;
    private isVisible = true;
    private isSortable = true;
    private position = 1;
    private dataAccessor: undefined | DataAccessorFunction = undefined;
    private optionalValueAccessors: ColumnName[] = [];
    private renderCell: undefined | RenderCellFunction = undefined;
    private alignment: Alignment = 'left'
    private headlineAlignment: Alignment = 'center'
    private growBy: number = 1
    private className: DataAccessClassNameFunction | undefined = undefined;
    private headlineCssClass: string | undefined = undefined;

    /**
     * Sets a custom data accessor function for the column.
     * @param fun - The function used to extract and format data from a row.
     */
    setDataAccessor( fun: DataAccessorFunction ): ColumnBuilder {
        this.dataAccessor = fun;
        return this;
    }

    /**
     * Sets the column type to 'money' for currency formatting.
     */
    typeMoney(): ColumnBuilder {
        this.type = COLUMN_TYPES.money;
        return this;
    }

    /**
     * Sets the column type to 'number' for numeric formatting.
     */
    typeNumber(): ColumnBuilder {
        this.type = COLUMN_TYPES.number;
        return this;
    }

    /**
     * Sets the column type to 'download' for rendering a download action.
     */
    typeDownload(): ColumnBuilder {
        this.type = COLUMN_TYPES.download;
        return this;
    }

    /**
     * Sets the column type to 'address' for address formatting.
     */
    typeAddress(): ColumnBuilder {
        this.type = COLUMN_TYPES.address;
        return this;
    }

    /**
     * Sets the column type to 'detailLink' for rendering a link to a detail view.
     */
    typeDetailLink(): ColumnBuilder {
        this.type = COLUMN_TYPES.detailLink;
        return this;
    }

    /**
     * Sets the column type to 'reportSolvency' for solvency report actions.
     */
    typeReportSolvency(): ColumnBuilder {
        this.type = COLUMN_TYPES.reportSolvency;
        return this;
    }

    /**
     * Sets the column type to 'bool' for boolean/checkbox rendering.
     */
    typeBool(): ColumnBuilder {
        this.type = COLUMN_TYPES.bool;
        return this;
    }
    /**
     * Sets the column type to 'dateTime' for date and time formatting.
     */
    typeDateTime(): ColumnBuilder {
        this.type = COLUMN_TYPES.dateTime;
        return this;
    }

    /**
     * Sets the column type to 'date' for date-only formatting.
     */
    typeDate(): ColumnBuilder {
        this.type = COLUMN_TYPES.date;
        return this;
    }

    /**
     * Sets the column type to 'lockStatus' for lock status indicators.
     */
    typeLock(): ColumnBuilder {
        this.type = COLUMN_TYPES.lockStatus;
        return this;
    }

    /**
     * Sets the column type to 'renderCell' and provides a custom rendering function.
     * @param cellComponent - The function to render the cell's content.
     */
    typeRenderCell( cellComponent: RenderCellFunction ): ColumnBuilder {
        this.type = COLUMN_TYPES.renderCell;
        this.renderCell = cellComponent

        return this;
    }

    /**
     * Sets a custom column type using a numeric identifier.
     * @param type - The custom column type identifier.
     */
    typeOther( type: number ): ColumnBuilder {
        this.type = type;
        return this;
    }

    /**
     * Configures a function to dynamically determine the CSS class for cell data.
     * @param classNameFunc - The function that returns a CSS class name based on cell data.
     */
    withClassName( classNameFunc: DataAccessClassNameFunction ): ColumnBuilder {
        this.className = classNameFunc;
        return this;
    }

    /**
     * Sets the horizontal alignment for cell content.
     * @param align - The alignment type ('left', 'center', 'right').
     */
    align( align: Alignment ): ColumnBuilder {
        this.alignment = align;
        return this;
    }

    /**
     * Sets the horizontal alignment for the column headline.
     * @param align - The alignment type ('left', 'center', 'right').
     */
    headlineAlign( align: Alignment ): ColumnBuilder {
        this.headlineAlignment = align;
        return this;
    }

    /**
     * Adds a custom CSS class to the column headline.
     * @param className - The CSS class name.
     */
    withHeadlineCssClass( className: string ): ColumnBuilder {
        this.headlineCssClass = className;
        return this;
    }

    /**
     * Marks the column as visible.
     */
    visible(): ColumnBuilder {
        this.isVisible = true;
        return this;
    }

    /**
     * Marks the column as hidden.
     */
    notVisible(): ColumnBuilder {
        this.isVisible = false;
        return this;
    }

    /**
     * Marks the column as sortable.
     */
    sortable(): ColumnBuilder {
        this.isSortable = true;
        return this;
    }


    /**
     * Sets the flex-grow factor for the column width.
     * @param num - The grow factor.
     */
    grow( num: number ): ColumnBuilder {
        this.growBy = num;
        return this;
    }

    /**
     * Marks the column as not sortable.
     */
    notSortable(): ColumnBuilder {
        this.isSortable = false;
        return this;
    }

    /**
     * Sets the ordinal position of the column.
     * @param pos - The position index.
     */
    atPosition(pos: number): ColumnBuilder {
        this.position = pos;
        return this;
    }

    /**
     * Sets the display text for the column headline.
     * @param headline - The headline string.
     */
    withHeadline(headline: string): ColumnBuilder {
        this.headline = headline;
        return this;
    }

    /**
     * Sets the unique field name/identifier for the column.
     * @param name - The field name.
     */
    withName(name: string): ColumnBuilder {
        this.field = name;
        return this;
    }

    /**
     * Sets optional accessors for retrieving values used in complex cells.
     * @param accessors - Array of column names.
     */
    setOptionalValueAccessors(accessors: ColumnName[]): ColumnBuilder {
        this.optionalValueAccessors = accessors;
        return this;
    }

    /**
     * Resets all builder properties to their default values.
     */
    resetToDefault(): ColumnBuilder {
        this.dataAccessor = undefined;
        this.position = 1;
        this.headline = "";
        this.type = COLUMN_TYPES.plain;
        this.field = "";
        this.isVisible = true;
        this.isSortable = true;
        this.optionalValueAccessors = [];
        this.renderCell = undefined;
        this.alignment = 'left';
        this.headlineAlignment = 'center';
        this.growBy = 1;
        this.className = undefined;
        return this;
    }

    /**
     * Finalizes the column configuration and returns the Column object.
     * @returns The configured Column object.
     * @throws Error if field name or headline is empty.
     */
    build(): Column {
        if (this.field === "") {
            throw "Column name can not be empty!"
        }
        if (this.headline === "") {
            throw "column headline can not be empty"
        }

        const column = {
            field: this.field,
            headline: this.headline,
            type: this.type,
            isVisible: this.isVisible,
            isSortable: this.isSortable,
            position: this.position,
            dataAccessorFunction: this.dataAccessor,
            optionalValueAccessors: this.optionalValueAccessors,
            renderCell: this.renderCell,
            valueAlign: this.alignment,
            headlineAlign: this.headlineAlignment,
            growBy: this.growBy,
            className: this.className,
            headlineCssClass: this.headlineCssClass
        };

        this.resetToDefault();

        return column;
    }

}

/**
 * Represents the structure and behavior of a column in a data table.
 * This is used to define how data should appear, behave, and be accessed within a tabular format.
 *
 * The `Column` interface extends additional behaviors from multiple traits, adding functionality like sorting,
 * hiding, positioning, and custom cell rendering.
 *
 * @extends ColumnName Allows the identification and management of the column via a unique name.
 * @extends Sortable Provides sorting functionality for the column's data.
 * @extends Hideable Adds the ability to show or hide the column.
 * @extends Positionable Controls the column's position within the table.
 * @extends RenderCellProp Enables custom rendering settings for the column's cells.
 */
export interface Column extends ColumnName, Sortable, Hideable, Positionable, RenderCellProp {
    /** The display text for the column header. */
    headline: string,
    /** Optional CSS class applied to the header cell. */
    headlineCssClass?: string,
    /** The type of data in this column, affecting rendering. */
    type: number,
    /** Optional function to determine CSS classes for cell content. */
    className?: DataAccessClassNameFunction
    /** Function used to retrieve and format data for this column. */
    dataAccessorFunction?: DataAccessorFunction
    /** List of other columns whose data might be needed by this column's accessor. */
    optionalValueAccessors: ColumnName[]
    /** Horizontal alignment of the header text. */
    headlineAlign?: Alignment
    /** Horizontal alignment of the cell content. */
    valueAlign?: Alignment
    /** Flex-grow property for responsive width calculation. */
    growBy?: number
}

/**
 * Represents the name of a column in a dataset or table.
 *
 * This interface is designed to standardize the representation of a column's name,
 * ensuring consistent usage and enabling reliable mappings or field references in the application.
 */
export interface ColumnName {
    field: string
}

/**
 * Properties for components that require a list of columns.
 */
export interface ColumnListProp {
    /** The list of columns to be rendered or managed. */
    columns: ColumnList
}

/**
 * Properties for identifying the primary ID field in a row.
 */
export interface IdColumnProp {
    /** The name of the field that serves as the unique identifier for the row. */
    idFieldName: string
}

/**
 * Represents the current sorting state of the table.
 */
export type SortModel = SortItem[]

/**
 * Converts a SortModel into an OrderBy array for API requests.
 * @param model - The current sort model.
 * @returns Array of OrderBy objects.
 */
export function sortModelToOrderBy( model: SortModel ): OrderBy[] {

    console.log( model )
    return model.map( _sortMod => ({
        fieldName: _sortMod.field,
        direction: ( _sortMod.sort !== null && typeof _sortMod.sort !== 'undefined' )
            ? _sortMod.sort === 'asc'
            : true
    }));
}

/**
 * A single item in the sorting model.
 */
export interface SortItem extends ColumnName {
    /** The direction of sorting. */
    sort: SortDirection
}

/**
 * GET CURRENT SORT MODEL
 *
 * retrieves the SortModel from redux store UserViewSettingsList by viewIdentifier.
 * when not initialized yet, the functions returns an empty array
 *
 * this function always comes in handy when dealing with the sort list component
 *
 * @param userViewSettingsList
 * @param viewIdentifier
 */
export function getCurrentSortModelRedux(userViewSettingsList: UserViewSettingsList, viewIdentifier: string ): SortModel {
    if( userViewSettingsList[ viewIdentifier ] ) {
        return orderByToSortModel( userViewSettingsList[ viewIdentifier ].orderByFields )
    } else {
        return [];
    }
}


/**
 * ORDER BY TO SORT MODEL
 *
 * converts OrderBy array type to SortModel
 * @param orderBy array
 */
export function orderByToSortModel( orderBy: OrderBy[] ): SortModel {
    return orderBy.map( order => ({
        field: order.fieldName,
        sort: order.direction ? 'asc' : 'desc'
    }) );
}

/**
 * GET FILTERED LIST OF SORT ITEMS
 *
 * column list will be safely retrieved from redux store.
 * column list will then be filtered if the column is filterable
 * lastly the column will be converted to SortListItem
 *
 * this functions always comes in handy when dealing with the sort list component and data tables
 *
 * @param columns
 * @param viewIdentifier
 */
export function columnListToSortListItems( columns: ColumnListState, viewIdentifier: string ): SortListItem[] {
    if( columns[ viewIdentifier ] ) {
        return columnListToSortList( columns[ viewIdentifier ] );

    } else {
        return [];
    }
}

/**
 * Converts a list of Columns to a list of SortListItems for UI components.
 * @param columns - The list of columns to convert.
 * @returns Array of SortListItem objects for sortable columns.
 */
export function columnListToSortList( columns: ColumnList ): SortListItem[] {
    return columns.filter( col => col.isSortable )
        .map( col => ({ field: col.field ?? '', title: col.headline ?? '', sort: null }) );
}

/** The possible sort directions. */
export type SortDirection = 'asc' | 'desc' | null | undefined

/** Text alignment options. */
export type Alignment = 'center' | 'left' | 'right'

/** Parameters for page change events in paginated tables. */
export interface PageChangeParams extends ViewIdentifiable {
    /** The new current page index. */
    page: number;
    /** The total number of pages available. */
    pageCount: number;
    /** The number of rows per page. */
    pageSize: number;
    /** The total number of rows across all pages. */
    rowCount: number;
}

/** Callback function signature for handling page changes. */
export type PageChangeFunction = ( params: PageChangeParams ) => void

/** Function signature for extracting data from a row for cell rendering. */
export type DataAccessorFunction<T = any> = ( data: CellDataAccessorProps<T> ) => ReactNode

/** Function signature for dynamically determining a CSS class for a cell. */
export type DataAccessClassNameFunction< T = any > = ( data: CellDataAccessorProps<T> ) => string

/** Properties passed to data accessors and cell renderers. */
export interface CellDataAccessorProps<T = any> extends ColumnControlled, LanguageProp {
    /** The raw data object for the current row. */
    rowData: T
}

/** Function signature for custom cell rendering. */
export type RenderCellFunction = ( data: CellDataAccessorProps ) => React.ReactNode

/** Function signature for rendering a button within a row. */
export type RenderRowButton = ( rowData: any, dataId: number ) => React.ReactNode

/** Properties for an action button within a table row. */
export interface RowActionButton {
    /** Callback fired when the button is clicked. */
    onClick: ( dataId: number ) => void
    /** Whether the user has permission to see/interact with this button. */
    isPrivileged: boolean
    /** The row data associated with the button. */
    rowData: any
    /** The unique identifier for the row. */
    dataId: number
}

/** Properties for cell click events. */
export interface CellClickProps extends ColumnControlled {
    /** The data contained within the clicked cell. */
    cellData: any
    /** The unique identifier for the row. */
    dataId: number
}

/** Callback function signature for cell click events. */
export type CellClickFunction = ( data: CellClickProps ) => void

/** Property for components that support custom cell rendering. */
export interface RenderCellProp {
    /** Optional custom rendering function for cells in this column. */
    renderCell?: RenderCellFunction
}

/** Type alias for an array of columns. */
export type ColumnList = Column[]

/** Basic properties for a table row component. */
export interface RowBasicProps extends ColumnListProp, Denseable {
    /** Number of columns that should remain fixed during horizontal scroll. */
    numberOfFixedColumns?: number
    /** Whether to display a dedicated column for action buttons. */
    showTableButtonsColumn?: boolean
    /** Callback for when a cell in the row is clicked. */
    onCellClick?: CellClickFunction
}

/** Property for components that are controlled by a specific column definition. */
export interface ColumnControlled {
    /** The column definition controlling this component. */
    column: Column
}

/**
 * calculates the standard width of one column, based on the column count
 * and the growBy property of the column
 *
 * @param column
 * @param columnCount
 */
export function calcColumnWidth( column: Column, columnCount: number  ): number {
    const growBy = column.growBy || 1;
    const standardWidth = 100 / columnCount;
    return Math.round( ( growBy <= 0 ) ? standardWidth : standardWidth * growBy );
}

/** Properties for the base cell component in the data table. */
export interface CellBaseProps extends ColumnControlled, Denseable {
    /** The column definition for this cell. */
    column: Column
    /** The raw data for the entire row. */
    rowData: any
    /** The specific data for this cell (extracted via accessor). */
    cellData: any
    /** The unique identifier for the row. */
    dataId: number
    /** Inline styles for the cell. */
    style?: React.CSSProperties
    /** Custom CSS class for the cell. */
    customClass?: string
    /** Total number of fixed columns in the table. */
    fixedColumnCount?: number
    /** Callback for cell click events. */
    onCellClick?: CellClickFunction
    /** Total number of columns in the row. */
    columnCount: number
}

/** Settings for standard action buttons (Edit, Delete, etc.) in a table. */
export interface TableButtonActionSettings {
    /** Whether a download action is available. */
    isDownloadable?: boolean
    /** Whether an edit action is available. */
    isEditable?: boolean
    /** Whether a delete action is available. */
    isDeleteable?: boolean
    /** Whether a detail view action is available. */
    hasDetail?: boolean
    /** Custom content/message for the delete confirmation dialog. */
    confirmDeleteContent?: React.ReactNode
    /** Callback for the delete action. */
    deleteAction?: (id: number) => void
    /** Callback for the edit action. */
    editAction?: (id: number) => void
    /** Callback for the download action. */
    downloadAction?: (id: number) => void
    /** Callback for the detail action. */
    detailAction?: (id: number) => void
}